import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ProgressBar } from './ProgressBar'
import { DynamicField } from './DynamicField'
import type { PasoPublico } from '../../types'

interface MultiStepFormProps {
  pasos: PasoPublico[]
  mensajeBienvenida?: string
  tiempoEstimado?: number
  imagenUrl?: string | null
  titulo: string
  descripcion?: string
  onSubmit: (respuestas: Record<string, unknown>) => Promise<void>
}

export function MultiStepForm({
  pasos,
  mensajeBienvenida,
  imagenUrl,
  titulo,
  descripcion,
  onSubmit,
}: MultiStepFormProps) {
  const [currentStep, setCurrentStep] = useState(0) // 0 = bienvenida
  const [respuestas, setRespuestas] = useState<Record<string, unknown>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const totalPasos = pasos.length
  const isWelcome = currentStep === 0
  const isLastStep = currentStep === totalPasos
  const pasoActual = !isWelcome ? pasos[currentStep - 1] : null

  const updateRespuesta = (nombreCampo: string, valor: unknown) => {
    setRespuestas((prev) => ({ ...prev, [nombreCampo]: valor }))
    setErrors([])
  }

  const getProvinciaSeleccionada = (): string | undefined => {
    if (!pasoActual) return undefined
    const campoProvincia = pasoActual.campos.find((c) => c.tipo_campo === 'provincias_ecuador')
    if (campoProvincia) return respuestas[campoProvincia.nombre_campo] as string | undefined
    for (const paso of pasos) {
      const cp = paso.campos.find((c) => c.tipo_campo === 'provincias_ecuador')
      if (cp) return respuestas[cp.nombre_campo] as string | undefined
    }
    return undefined
  }

  const validateStep = (): boolean => {
    if (!pasoActual) return true
    const camposRequeridos = pasoActual.campos.filter((c) => c.requerido)
    const missing: string[] = []
    for (const campo of camposRequeridos) {
      const val = respuestas[campo.nombre_campo]
      if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
        missing.push(campo.etiqueta)
      }
    }
    if (missing.length > 0) {
      setErrors(missing)
      return false
    }
    return true
  }

  const handleNext = () => {
    if (!validateStep()) return
    setErrors([])
    setCurrentStep((prev) => prev + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePrev = () => {
    setErrors([])
    setCurrentStep((prev) => prev - 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSubmit = async () => {
    if (!validateStep()) return
    setIsSubmitting(true)
    try {
      await onSubmit(respuestas)
    } catch {
      setErrors(['Error al enviar. Intenta de nuevo.'])
    } finally {
      setIsSubmitting(false)
    }
  }

  // Pantalla de bienvenida
  if (isWelcome) {
    return (
      <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-6 sm:p-8">
        {/* Campaign Image */}
        {imagenUrl && (
          <div className="text-center mb-8">
            <img
              src={imagenUrl}
              alt={titulo}
              className="mx-auto max-w-full h-auto max-h-48"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          </div>
        )}

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-bold text-gray-900 mb-4 flex items-center justify-center text-2xl">
            <svg className="w-8 h-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            {titulo}
          </h1>
          {descripcion && (
            <p className="text-lg text-gray-600">{descripcion}</p>
          )}
        </div>

        {/* Welcome Message */}
        {mensajeBienvenida && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-blue-800 mb-1">Mensaje de Bienvenida</h3>
                <p className="text-sm text-blue-700 whitespace-pre-line">{mensajeBienvenida}</p>
              </div>
            </div>
          </div>
        )}

        {/* Start Button */}
        <div className="text-center">
          <button
            onClick={() => setCurrentStep(1)}
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-blue-700 hover:bg-blue-800 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            Comenzar Formulario
          </button>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-4 mb-8 mt-5">
          <div className="bg-white rounded-lg p-6 border border-gray-200 text-center shadow-sm">
            <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-1">1 minuto</p>
            <p className="text-sm text-gray-600">Tiempo estimado</p>
          </div>

          <div className="bg-white rounded-lg p-6 border border-gray-200 text-center shadow-sm">
            <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-1">100% Seguro</p>
            <p className="text-sm text-gray-600">Datos protegidos</p>
          </div>

          <div className="bg-white rounded-lg p-6 border border-gray-200 text-center shadow-sm">
            <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-900 mb-1">Paso a paso</p>
            <p className="text-sm text-gray-600">Fácil de completar</p>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-center text-center">
              <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-gray-600">
                Tus datos están protegidos y solo se utilizarán para procesar tu solicitud de muestra.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-4 sm:p-6 lg:p-8">
      <ProgressBar
        currentStep={currentStep}
        totalSteps={totalPasos}
        imagenUrl={imagenUrl}
        titulo={titulo}
      />

      {/* Step Header */}
      <div className="bg-blue-50 rounded-lg p-4 sm:p-6 border border-blue-200 mb-6 sm:mb-8 mt-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center flex-wrap">
          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mr-2 sm:mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {pasoActual?.titulo}
        </h1>
        {pasoActual?.descripcion && (
          <p className="text-blue-700 text-sm text-center">{pasoActual.descripcion}</p>
        )}
      </div>

      {/* Fields */}
      <div className="space-y-4 sm:space-y-5">
        {pasoActual?.campos.map((campo) => (
          <DynamicField
            key={campo.id}
            campo={campo}
            value={respuestas[campo.nombre_campo]}
            onChange={(val) => updateRespuesta(campo.nombre_campo, val)}
            provinciaSeleccionada={campo.tipo_campo === 'ciudades_ecuador' ? getProvinciaSeleccionada() : undefined}
          />
        ))}

        {errors.length > 0 && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md flex items-start">
            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>
              {errors[0] === 'Error al enviar. Intenta de nuevo.'
                ? errors[0]
                : <>Campos requeridos: {errors.join(', ')}</>
              }
            </span>
          </div>
        )}

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
          <div className="w-full sm:flex-1 order-2 sm:order-1">
            <button
              onClick={handlePrev}
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 sm:px-6 py-3 border border-gray-300 rounded-lg text-sm sm:text-base font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Anterior
            </button>
          </div>

          {isLastStep ? (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full sm:w-auto order-1 sm:order-2 inline-flex items-center justify-center px-6 sm:px-8 py-3 border border-transparent rounded-lg text-sm sm:text-base font-semibold text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <>
                  Enviar Solicitud
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </>
              )}
              {isSubmitting && 'Enviando...'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="w-full sm:w-auto order-1 sm:order-2 inline-flex items-center justify-center px-6 sm:px-8 py-3 border border-transparent rounded-lg text-sm sm:text-base font-semibold text-white bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Siguiente
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Campaign Description */}
      {descripcion && (
        <div className="mt-4 sm:mt-6 bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
          <div className="flex items-center justify-center text-center">
            <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-gray-600 text-xs sm:text-sm">{descripcion}</p>
          </div>
        </div>
      )}
    </div>
  )
}
